import { useRef, useState, useEffect } from "react";
import { MediaUploadManager } from "../../../media/components/MediaUploadManager";
import ThumbnailUploader from "../../../thumbnail/components/ThumbnailUploader";
import Modal from '../../../../components/Modal';
import { useModal } from '../../../../hooks/useModal';
import Button from '../../../../components/Button';
import { usePinValidation } from '../../hooks/marker/usePinValidation';
import { formFieldClass, formShell } from '../../../../styles/formShell';
import { markerEditorShell, markerEditorTabClass } from '../../../../styles/markerEditorShell';

function MarkerEditor({
    marker,
    labelOptions,
    onChange,
    onSavePin,
    onDelete,
    onClose,
    tmpMediaList,
    addMedia,
    uploadMedia,
    deleteMedia,
    removeFileAt
}) {
    const containerRef = useRef(null);
    const { isOpen, openModal, closeModal } = useModal();
    const { errors, validatePin, clearFieldError } = usePinValidation();
    const [activeTab, setActiveTab] = useState('basic');
    const hasNoLabelOption = labelOptions.some(label => label.name === "ラベルなし");
    const sortedLabelOptions = [...labelOptions].sort((a, b) => {
        if (a.name === "ラベルなし") return -1;
        if (b.name === "ラベルなし") return 1;
        return 0;
    });

    // Delete confirmation modal logic
    const handleDelete = () => {
        openModal();
    };
    const handleConfirmDelete = () => {
        closeModal();
        onDelete();
    };

    const mediaListHasMissingTitles = (list) =>
        Array.isArray(list) && list.some((m) => !m?.name?.trim());

    // Save with validation
    const handleSave = () => {
        const pinOk = validatePin(marker);
        if (!pinOk) {
            setActiveTab('basic');
            return;
        }

        if (mediaListHasMissingTitles(tmpMediaList)) {
            setActiveTab('media');
            return;
        }

        onSavePin();
    };

    // Handle field changes with error clearing
    const handleFieldChange = (field, value) => {
        onChange({ [field]: value });
        if (errors[field]) {
            clearFieldError(field);
        }
    };

    // タイトル未入力時は保存不可（警告は初期検証で表示）
    const isSaveDisabled = !marker.title?.trim();

    // 初期／更新時に「タイトルは必須です」を表示（従来どおり）
    useEffect(() => {
        validatePin(marker);
    }, [marker, validatePin]);

    return (
        <>
            <div className={markerEditorShell.wrapper}>
                {onClose && (
                    <button
                        type="button"
                        className={markerEditorShell.closeButton}
                        onClick={onClose}
                        aria-label="閉じる"
                    >
                        <span className="material-symbols-outlined text-icon-md">
                            close
                        </span>
                    </button>
                )}
                <div ref={containerRef} className={markerEditorShell.container}>
                    <div className={markerEditorShell.headerRow}>
                        <div className={markerEditorShell.headerLeft}>
                            <h3 className={markerEditorShell.header}>ピンの編集</h3>
                            {marker.creatorName && (
                                <div className={markerEditorShell.creatorInfo}>
                                    <span className={markerEditorShell.metaLabel}>作成者: </span>
                                    <span className={markerEditorShell.metaValue}>{marker.creatorName}</span>
                                </div>
                            )}
                        </div>
                        <div className={markerEditorShell.headerActions}>
                            <div className={markerEditorShell.tabs}>
                                <button
                                    type="button"
                                    className={markerEditorTabClass(activeTab === 'basic')}
                                    onClick={() => setActiveTab('basic')}
                                >
                                    <span className="material-symbols-outlined">info</span>
                                    基本情報
                                </button>
                                <button
                                    type="button"
                                    className={markerEditorTabClass(activeTab === 'media')}
                                    onClick={() => setActiveTab('media')}
                                >
                                    <span className="material-symbols-outlined">image</span>
                                    メディア
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={markerEditorShell.content}>
                        {/* 基本情報タブ */}
                        {activeTab === 'basic' && (
                            <div className={markerEditorShell.section}>
                                <div className={markerEditorShell.formRow}>
                                    <div className={`${formShell.group} min-w-0 flex-1`}>
                                        <label className={formShell.label}>タイトル</label>
                                        <input
                                            type="text"
                                            value={marker.title || ""}
                                            onChange={(e) => handleFieldChange('title', e.target.value)}
                                            className={`${formFieldClass(Boolean(errors.title))} ${markerEditorShell.field}`}
                                            placeholder="例：〇〇の記念碑"
                                            aria-invalid={Boolean(errors.title)}
                                            aria-describedby="pin-title-error"
                                        />
                                        <p
                                            id="pin-title-error"
                                            className={`${formShell.error} ${markerEditorShell.errorSlot} ${errors.title ? "" : "invisible"}`}
                                            role={errors.title ? "alert" : undefined}
                                        >
                                            {errors.title || "タイトルは必須です"}
                                        </p>
                                    </div>

                                    <div className={`${formShell.group} min-w-0 flex-1`}>
                                        <label className={formShell.label}>ラベル</label>
                                        <select
                                            className={`${formFieldClass(false, 'select')} ${markerEditorShell.field}`}
                                            value={marker.labelId ?? ""}
                                            onChange={(e) =>
                                                handleFieldChange('labelId', e.target.value ? Number(e.target.value) : null)
                                            }
                                        >
                                            {!hasNoLabelOption && (
                                                <option value="">― ラベルを選択 ―</option>
                                            )}
                                            {sortedLabelOptions.map((label) => (
                                                <option key={label.id} value={label.id} style={{ color: label.color }}>
                                                    {label.name}
                                                </option>
                                            ))}
                                        </select>
                                        <p className={`${formShell.error} ${markerEditorShell.errorSlot} invisible`} aria-hidden="true">
                                            タイトルは必須です
                                        </p>
                                    </div>
                                </div>

                                <div className={markerEditorShell.thumbnailSection}>
                                    <ThumbnailUploader
                                        thumbnailURL={marker.thumbnail_url}
                                        thumbnailFile={marker.thumbnail}
                                        onThumbnailChange={(file) => {
                                            onChange({ thumbnail: file });
                                        }}
                                        onRemoveThumbnail={() => {
                                            onChange({ thumbnail: null, thumbnail_url: null });
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* メディアタブ */}
                        {activeTab === 'media' && (
                            <div className={markerEditorShell.section}>
                                <MediaUploadManager
                                    mediaList={tmpMediaList}
                                    addMedia={addMedia}
                                    uploadMedia={uploadMedia}
                                    deleteMedia={deleteMedia}
                                    removeFileAt={removeFileAt}
                                    maxFiles={15}
                                    markerId={marker.id || null}
                                />
                            </div>
                        )}

                        {/* 保存・削除ボタン */}
                        <div className={markerEditorShell.finalActions}>
                            {onDelete && (
                                <Button
                                    onClick={handleDelete}
                                    variant="danger"
                                    size="lg"
                                    className={markerEditorShell.buttonDeleteBottom}
                                    title="削除"
                                >
                                    <span className="material-symbols-outlined mr-1 align-middle text-icon-control">
                                        delete_forever
                                    </span>
                                    削除
                                </Button>
                            )}
                            <Button
                                onClick={handleSave}
                                variant="primary"
                                size="lg"
                                className={markerEditorShell.buttonSave}
                                disabled={isSaveDisabled}
                            >
                                <span className="material-symbols-outlined mr-1 align-middle text-icon-control">
                                    save
                                </span>
                                保存
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                title="ピンを削除"
                size="small"
                actions={[
                    { label: "キャンセル", variant: "secondary" },
                    { label: "削除", variant: "danger", onClick: handleConfirmDelete }
                ]}
            >
                <p>このピンを削除しますか？</p>
                <p>この操作は取り消せません。</p>
            </Modal>
        </>
    );
}

export default MarkerEditor;
